import { computed, ComputedRef, reactive } from 'vue'

export enum NotificationType {
    SUCCESS = 'success',
    FAIL = 'fail',
}

export interface NotificationOptions {
    slot?: string
    timeout?: number
    dontRemove?: boolean
}

export interface Notification {
    id: number
    type: NotificationType
    content: string
    slot: string
}

const DEFAULT_TIMEOUT = 3000
const DEFAULT_LIMIT = 3

const notifications: { list: Notification[] } = reactive({ list: [] })

export const useNotifications = (
    slot = 'default',
): {
    notifications: ComputedRef<Notification[]>
    pushSuccess: (content: string, options?: NotificationOptions) => void
    pushFail: (content: string, options?: NotificationOptions) => void
    removeNotification: (id: number) => void
    removeNotifications: () => void
} => {
    const pushNotification = (
        type: NotificationType,
        content: string,
        options?: NotificationOptions,
    ) => {
        // filter notofications from current slot
        const slotNotifications = notifications.list.filter(item => item.slot === slot)
        // set limit to 0 if slot is not global
        const limit = slot === 'default' ? DEFAULT_LIMIT : 1
        // remove if too much
        if (slotNotifications.length >= limit) {
            // remove last
            removeNotification(slotNotifications.reverse()[0].id)
        }
        // id based on timestamp
        const id = Date.now()
        // push new notification
        notifications.list.push({
            id,
            type,
            content,
            slot,
        })
        if (!options?.dontRemove) {
            setTimeout(() => {
                removeNotification(id)
            }, options?.timeout || DEFAULT_TIMEOUT)
        }
    }
    const removeNotification = (id: number) => {
        const i = notifications.list.map(item => item.id).indexOf(id)
        if (i > -1) {
            notifications.list.splice(i, 1)
        }
    }
    const removeNotifications = () => {
        notifications.list = []
    }
    return {
        notifications: computed(() => notifications.list.filter(item => item.slot === slot)),
        pushSuccess: (content: string, options?: NotificationOptions) =>
            pushNotification(NotificationType.SUCCESS, content, { ...options }),
        pushFail: (content: string, options?: NotificationOptions) =>
            pushNotification(NotificationType.FAIL, content, { ...options }),
        removeNotification,
        removeNotifications,
    }
}
